import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { ListingCard } from "@/components/ListingCard";
import { Loader2, Store, User, ShieldCheck, CarFront } from "lucide-react";

function Stat({ label, value, testId }) {
  return (
    <div className="border-l-2 border-accent pl-4">
      <div className="font-heading text-3xl font-bold" data-testid={testId}>{value}</div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

export default function SellerProfile() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [make, setMake] = useState("");

  useEffect(() => { api.get(`/sellers/${id}`).then(({ data }) => setS(data)).catch(() => setS(false)); }, [id]);
  const shown = useMemo(() => (s?.listings || []).filter((l) => !make || l.make === make), [s, make]);

  if (s === false) return <div className="max-w-3xl mx-auto px-6 py-24 text-center text-muted-foreground" data-testid="seller-not-found">Seller not found.</div>;
  if (!s) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-accent" /></div>;

  const dealer = s.seller_type === "dealer";
  const since = s.member_since ? new Date(s.member_since).toLocaleDateString("en-GB", { month: "long", year: "numeric" }) : "—";

  return (
    <div data-testid="seller-profile-page">
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-[0.2em] font-semibold">
            {dealer ? <Store className="w-4 h-4" /> : <User className="w-4 h-4" />}
            {dealer ? "Dealer forecourt" : "Private seller"}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold mt-3" data-testid="seller-display-name">{s.display_name}</h1>
          <p className="text-slate-300 mt-2 text-sm">On Vorra since {since}</p>
        </div>
      </section>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid grid-cols-3 gap-6 max-w-xl">
          <Stat label="Cars for sale" value={s.live_count} testId="seller-live-count" />
          <Stat label="Verified" value={s.verified_count} testId="seller-verified-count" />
          <Stat label="Sold on Vorra" value={s.sold_count} testId="seller-sold-count" />
        </div>
        {s.makes.length > 1 && (
          <div className="flex flex-wrap gap-2 mt-10" data-testid="seller-make-filter">
            {["", ...s.makes].map((m) => (
              <button key={m || "all"} onClick={() => setMake(m)} data-testid={`seller-make-${m || "all"}`}
                className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${make === m ? "bg-primary text-white border-primary" : "border-border hover:border-accent"}`}>
                {m || "All makes"}
              </button>
            ))}
          </div>
        )}
        {shown.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="seller-listings-grid">
            {shown.map((l) => <ListingCard key={l.id} listing={l} />)}
          </div>
        ) : (
          <div className="mt-12 text-center text-muted-foreground py-16 border border-dashed rounded-xl" data-testid="seller-empty">
            <CarFront className="w-8 h-8 mx-auto mb-2" />No cars for sale right now.
          </div>
        )}
        <p className="mt-12 text-xs text-muted-foreground flex items-start gap-2 max-w-2xl">
          <ShieldCheck className="w-4 h-4 shrink-0" />
          Vorra is an intermediary and is not a party to any sale. Each seller is responsible for their vehicles' condition, description and accuracy.
        </p>
      </div>
    </div>
  );
}
